import { useMemo, useState } from "react";
import { useUnits } from "../context/UnitContext";
import {
    calculateWorkoutFluidMl,
    calculateKidWaterMl,
    calculateElderlyWaterMl,
    formatVolume,
    lbsToKg,
    mlToFloz,
    flozToMl,
    mlToL,
    flozToCups,
} from "../lib/calculations";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

const Card = ({ children, testId }) => (
    <div className="bg-card border border-foreground p-6 sm:p-10 hard-shadow-sm" data-testid={testId}>
        {children}
    </div>
);

const Result = ({ label, value, sub }) => (
    <div className="bg-foreground text-background p-6 mt-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-background/60 mb-2">{label}</div>
        <div className="text-4xl sm:text-5xl font-black tracking-tighter">{value}</div>
        {sub && <div className="text-xs font-mono uppercase tracking-widest text-background/60 mt-2">{sub}</div>}
    </div>
);

const WorkoutCalculator = () => {
    const { units } = useUnits();
    const [minutes, setMinutes] = useState(45);
    const [intensity, setIntensity] = useState("moderate");
    const ml = useMemo(() => calculateWorkoutFluidMl({ minutes: Number(minutes), intensity }), [minutes, intensity]);

    return (
        <Card testId="workout-calc">
            <div className="editorial-label mb-2">Workout hydration</div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-6">Add this on top of your daily target.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                    <label className="editorial-label block mb-2">Duration (minutes)</label>
                    <input
                        type="number"
                        data-testid="workout-minutes"
                        value={minutes}
                        min="5"
                        max="600"
                        onChange={(e) => setMinutes(e.target.value)}
                        className="editorial-input"
                    />
                </div>
                <div>
                    <label className="editorial-label block mb-2">Intensity</label>
                    <div className="grid grid-cols-3 gap-px bg-foreground border border-foreground">
                        {["light", "moderate", "vigorous"].map((i) => (
                            <button
                                key={i}
                                type="button"
                                data-testid={`workout-intensity-${i}`}
                                onClick={() => setIntensity(i)}
                                className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                    intensity === i ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                                }`}
                            >
                                {i}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <Result
                label="Extra fluids during workout"
                value={formatVolume(ml, units)}
                sub={units === "metric" ? `${mlToFloz(ml).toFixed(1)} fl oz` : `${ml} ml`}
            />
            <p className="text-xs font-mono uppercase tracking-widest text-foreground/60 mt-4">
                Sip 150-300 ml every 15-20 minutes during exercise
            </p>
        </Card>
    );
};

const PregnancyCalculator = () => {
    const { units } = useUnits();
    const [weight, setWeight] = useState(units === "metric" ? 65 : 143);
    const [stage, setStage] = useState("pregnant");

    const weightKg = units === "metric" ? Number(weight) : lbsToKg(Number(weight));
    const baseMl = weightKg * 35;
    const addMl = stage === "pregnant" ? 300 : stage === "breastfeeding" ? 700 : 0;
    const totalMl = Math.round(baseMl + addMl);

    return (
        <Card testId="pregnancy-calc">
            <div className="editorial-label mb-2">Pregnancy & breastfeeding</div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-6">Hydration for two.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                    <label className="editorial-label block mb-2">Weight ({units === "metric" ? "kg" : "lbs"})</label>
                    <input
                        type="number"
                        data-testid="pregnancy-weight"
                        value={weight}
                        min="30"
                        onChange={(e) => setWeight(e.target.value)}
                        className="editorial-input"
                    />
                </div>
                <div>
                    <label className="editorial-label block mb-2">Stage</label>
                    <div className="grid grid-cols-2 gap-px bg-foreground border border-foreground">
                        <button
                            type="button"
                            data-testid="pregnancy-stage-pregnant"
                            onClick={() => setStage("pregnant")}
                            className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                stage === "pregnant" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                            }`}
                        >
                            Pregnant
                        </button>
                        <button
                            type="button"
                            data-testid="pregnancy-stage-breastfeeding"
                            onClick={() => setStage("breastfeeding")}
                            className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                stage === "breastfeeding" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                            }`}
                        >
                            Breastfeeding
                        </button>
                    </div>
                </div>
            </div>
            <Result
                label="Daily target"
                value={formatVolume(totalMl, units)}
                sub={`+${addMl} ml above baseline`}
            />
        </Card>
    );
};

const KidsElderlyCalculator = () => {
    const { units } = useUnits();
    const [mode, setMode] = useState("kids");
    const [weight, setWeight] = useState(units === "metric" ? 25 : 55);
    const weightKg = units === "metric" ? Number(weight) : lbsToKg(Number(weight));
    const ml = mode === "kids" ? calculateKidWaterMl(weightKg) : calculateElderlyWaterMl(weightKg);

    return (
        <Card testId="kids-elderly-calc">
            <div className="editorial-label mb-2">Kids & elderly</div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-6">Adjusted for life stage.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                    <label className="editorial-label block mb-2">Group</label>
                    <div className="grid grid-cols-2 gap-px bg-foreground border border-foreground">
                        <button
                            type="button"
                            data-testid="ke-mode-kids"
                            onClick={() => setMode("kids")}
                            className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                mode === "kids" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                            }`}
                        >
                            Children
                        </button>
                        <button
                            type="button"
                            data-testid="ke-mode-elderly"
                            onClick={() => setMode("elderly")}
                            className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                mode === "elderly" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                            }`}
                        >
                            Elderly (65+)
                        </button>
                    </div>
                </div>
                <div>
                    <label className="editorial-label block mb-2">Weight ({units === "metric" ? "kg" : "lbs"})</label>
                    <input
                        type="number"
                        data-testid="ke-weight"
                        value={weight}
                        min="5"
                        onChange={(e) => setWeight(e.target.value)}
                        className="editorial-input"
                    />
                </div>
            </div>
            <Result
                label="Daily target"
                value={formatVolume(ml, units)}
                sub={mode === "kids" ? "Holliday-Segar formula" : "Adjusted for kidney function"}
            />
        </Card>
    );
};

const UnitConverter = () => {
    const [value, setValue] = useState(1000);
    const [fromUnit, setFromUnit] = useState("ml");

    const ml = useMemo(() => {
        const v = Number(value) || 0;
        switch (fromUnit) {
            case "ml": return v;
            case "L": return v * 1000;
            case "floz": return flozToMl(v);
            case "cups": return v * 240; // 1 US cup = 240 ml
            default: return v;
        }
    }, [value, fromUnit]);

    const conversions = [
        { label: "Milliliters (ml)", val: ml.toFixed(0) },
        { label: "Liters (L)", val: mlToL(ml).toFixed(3) },
        { label: "US Fl Oz", val: mlToFloz(ml).toFixed(2) },
        { label: "US Cups", val: flozToCups(mlToFloz(ml)).toFixed(2) },
    ];

    return (
        <Card testId="unit-converter">
            <div className="editorial-label mb-2">Unit converter</div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-6">Volume comparison tool.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                    <label className="editorial-label block mb-2">Amount</label>
                    <input
                        type="number"
                        data-testid="converter-value"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="editorial-input"
                    />
                </div>
                <div>
                    <label className="editorial-label block mb-2">From</label>
                    <div className="grid grid-cols-4 gap-px bg-foreground border border-foreground">
                        {[
                            { v: "ml", l: "ml" },
                            { v: "L", l: "L" },
                            { v: "floz", l: "fl oz" },
                            { v: "cups", l: "cups" },
                        ].map((u) => (
                            <button
                                key={u.v}
                                type="button"
                                data-testid={`converter-from-${u.v}`}
                                onClick={() => setFromUnit(u.v)}
                                className={`px-2 py-2.5 text-xs font-mono uppercase tracking-widest ${
                                    fromUnit === u.v ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                                }`}
                            >
                                {u.l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-px bg-foreground border border-foreground">
                {conversions.map((c, i) => (
                    <div key={i} className="bg-card p-4">
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-foreground/60 mb-2">
                            {c.label}
                        </div>
                        <div className="text-2xl sm:text-3xl font-black tracking-tighter" data-testid={`converter-result-${i}`}>
                            {c.val}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export const SubCalculators = () => (
    <Tabs defaultValue="workout" className="w-full" data-testid="sub-calculators">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full bg-foreground p-0 h-auto rounded-none border border-foreground gap-px">
            <TabsTrigger
                value="workout"
                data-testid="tab-workout"
                className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white text-xs font-mono uppercase tracking-widest py-3 bg-card text-foreground"
            >
                Workout
            </TabsTrigger>
            <TabsTrigger
                value="pregnancy"
                data-testid="tab-pregnancy"
                className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white text-xs font-mono uppercase tracking-widest py-3 bg-card text-foreground"
            >
                Pregnancy
            </TabsTrigger>
            <TabsTrigger
                value="kids"
                data-testid="tab-kids"
                className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white text-xs font-mono uppercase tracking-widest py-3 bg-card text-foreground"
            >
                Kids / Elderly
            </TabsTrigger>
            <TabsTrigger
                value="converter"
                data-testid="tab-converter"
                className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white text-xs font-mono uppercase tracking-widest py-3 bg-card text-foreground"
            >
                Converter
            </TabsTrigger>
        </TabsList>
        <TabsContent value="workout" className="mt-8"><WorkoutCalculator /></TabsContent>
        <TabsContent value="pregnancy" className="mt-8"><PregnancyCalculator /></TabsContent>
        <TabsContent value="kids" className="mt-8"><KidsElderlyCalculator /></TabsContent>
        <TabsContent value="converter" className="mt-8" id="converter"><UnitConverter /></TabsContent>
    </Tabs>
);
